import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import moment from "moment";
import { getGreekCurrencyFormat, getDOYOptions } from "../../../../_helpers/commonFunctions";

const GREEK_MONTHS = [
    "ΙΑΝΟΥΑΡΙΟΣ", "ΦΕΒΡΟΥΑΡΙΟΣ", "ΜΑΡΤΙΟΣ", "ΑΠΡΙΛΙΟΣ", "ΜΑΪΟΣ", "ΙΟΥΝΙΟΣ",
    "ΙΟΥΛΙΟΣ", "ΑΥΓΟΥΣΤΟΣ", "ΣΕΠΤΕΜΒΡΙΟΣ", "ΟΚΤΩΒΡΙΟΣ", "ΝΟΕΜΒΡΙΟΣ", "ΔΕΚΕΜΒΡΙΟΣ"
];

const EU_COUNTRIES = [
    { label: "ΑΥΣΤΡΙΑ", code: "AT" },
    { label: "ΒΕΛΓΙΟ", code: "BE" },
    { label: "ΒΟΥΛΓΑΡΙΑ", code: "BG" },
    { label: "Β. ΙΡΛΑΝΔΙΑ", code: "XI" },
    { label: "ΓΑΛΛΙΑ", code: "FR" },
    { label: "ΓΕΡΜΑΝΙΑ", code: "DE" },
    { label: "ΔΑΝΙΑ", code: "DK" },
    { label: "ΕΣΘΟΝΙΑ", code: "EE" },
    { label: "ΙΡΛΑΝΔΙΑ", code: "IE" },
    { label: "ΙΣΠΑΝΙΑ", code: "ES" },
    { label: "ΙΤΑΛΙΑ", code: "IT" },
    { label: "ΚΡΟΑΤΙΑ", code: "HR" },
    { label: "ΚΥΠΡΟΣ", code: "CY" },
    { label: "ΛΕΤΟΝΙΑ", code: "LV" },
    { label: "ΛΙΘΟΥΑΝΙΑ", code: "LT" },
    { label: "ΛΟΥΞΕΜΒΟΥΡΓΟ", code: "LU" },
    { label: "ΜΑΛΤΑ", code: "MT" },
    { label: "ΟΛΛΑΝΔΙΑ", code: "NL" },
    { label: "ΟΥΓΓΑΡΙΑ", code: "HU" },
    { label: "ΠΟΛΩΝΙΑ", code: "PL" },
    { label: "ΠΟΡΤΟΓΑΛΙΑ", code: "PT" },
    { label: "ΡΟΥΜΑΝΙΑ", code: "RO" },
    { label: "ΣΛΟΒΑΚΙΑ", code: "SK" },
    { label: "ΣΛΟΒΕΝΙΑ", code: "SI" },
    { label: "ΣΟΥΗΔΙΑ", code: "SE" },
    { label: "ΤΣΕΧΙΚΗ ΔΗΜΟΚΡΑΤΙΑ", code: "CZ" },
    { label: "ΦΙΝΛΑΝΔΙΑ", code: "FI" },
];

const AccountingF5VatDeclarationView = ({ tabKey, declarationData }) => {
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);

    const reduxDeclaration = useSelector((state) => {
        if (!tabKey) return null;
        return state.ACCOUNTING_DATA?.selectedSubmittedF5Declarations?.[tabKey] ??
            state.ACCOUNTING_DATA?.selectedSubmittedVatDeclarations?.[tabKey] ?? null;
    });

    const decl = declarationData || reduxDeclaration || {};
    const fields = decl.fields || {};

    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragScrollLeft, setDragScrollLeft] = useState(0);

    const periodFrom = decl.periodStart || decl.date_from || fields["006_from"] || "";
    const periodTo = decl.periodEnd || decl.date_to || fields["006_to"] || "";
    const mFrom = moment(periodFrom, "DD/MM/YYYY");
    const mTo = moment(periodTo, "DD/MM/YYYY");
    const mSubmission = moment(decl.submissionDate || fields["004"] || moment(), "DD/MM/YYYY");

    const submissionDateSlash = mSubmission.isValid() ? mSubmission.format("DD/MM/YY") : "";
    const periodFromSlash = mFrom.isValid() ? mFrom.format("DD/MM/YY") : (periodFrom || "");
    const periodToSlash = mTo.isValid() ? mTo.format("DD/MM/YY") : (periodTo || "");

    const submissionYearShort = mSubmission.isValid() ? mSubmission.format("YY") : "";
    const selectedYear = mFrom.isValid() ? mFrom.format("YYYY") : (decl.year || "");
    const selectedMonthGreek = decl.month && Number(decl.month) >= 1 && Number(decl.month) <= 12
        ? GREEK_MONTHS[Number(decl.month) - 1]
        : (mFrom.isValid() ? GREEK_MONTHS[mFrom.month()] : "");
    const selectedQuarter = decl.month ? Math.ceil(Number(decl.month) / 3) : (mFrom.isValid() ? Math.ceil((mFrom.month() + 1) / 3) : 1);

    const doyOptions = getDOYOptions();
    let doyLabel = decl.doy || companyDetails?.doy || "";
    let doyCode = "";
    if (doyLabel) {
        const matchedDoy = doyOptions.find(opt =>
            String(opt.value) === String(doyLabel) ||
            String(opt.label) === String(doyLabel)
        );
        if (matchedDoy) {
            doyLabel = matchedDoy.label;
            doyCode = String(matchedDoy.value).padStart(4, "0");
        }
    }

    const isAmending = Boolean(
        decl.declarationType === "AMENDING" ||
        decl.isAmending === 1 ||
        decl.isAmending === true ||
        fields.isAmending === 1 ||
        fields["007"] === 1 ||
        fields["007"] === true ||
        fields["007"] === "1"
    );

    const rawTableB = (Array.isArray(decl.tableBData) && decl.tableBData.length > 0)
        ? decl.tableBData
        : (Array.isArray(fields.tableBData) && fields.tableBData.length > 0)
            ? fields.tableBData
            : (Array.isArray(decl.rows) ? decl.rows : []);

    const filledTableB = [...rawTableB];
    while (filledTableB.length < 25) {
        filledTableB.push({ ctryDesc: "", countryCode: "", bc4: "", bc5: "", bc6: "", bc7: "" });
    }

    const rawTotals = decl.totals || fields.totals || {};
    const sumBc5 = Number(rawTotals.bc5) > 0 ? Number(rawTotals.bc5) : filledTableB.reduce((acc, r) => acc + (parseFloat(String(r.bc5 || 0).replace(",", ".")) || 0), 0);
    const sumBc6 = Number(rawTotals.bc6) > 0 ? Number(rawTotals.bc6) : filledTableB.reduce((acc, r) => acc + (parseFloat(String(r.bc6 || 0).replace(",", ".")) || 0), 0);
    const sumBc7 = Number(rawTotals.bc7) > 0 ? Number(rawTotals.bc7) : filledTableB.reduce((acc, r) => acc + (parseFloat(String(r.bc7 || 0).replace(",", ".")) || 0), 0);

    const StrictCombReadOnly = ({ length, value = "", label = null }) => {
        const chars = String(value || "").padEnd(length, " ").slice(0, length).split("");

        return (
            <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", position: "relative" }}>
                {label && (
                    <div style={{
                        fontSize: "0.55rem",
                        fontWeight: "bold",
                        textAlign: "center",
                        color: "var(--f5-color)",
                        lineHeight: 1.2,
                        paddingTop: "2px"
                    }}>
                        {label}
                    </div>
                )}
                <div style={{ display: "flex", flexGrow: 1 }}>
                    {chars.map((char, idx) => (
                        <div key={idx} style={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "center"
                        }}>
                            <span style={{ fontSize: "0.8rem", fontWeight: "bold", color: "#000", paddingBottom: "2px" }}>
                                {char.trim()}
                            </span>
                            {idx < length - 1 && (
                                <div style={{
                                    position: "absolute",
                                    right: 0,
                                    bottom: 0,
                                    width: "1px",
                                    height: "5px",
                                    backgroundColor: "var(--f5-color)",
                                    zIndex: 1
                                }} />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const DateCombSlash = ({ value = "", color = "var(--f4-color)" }) => {
        let d = "  ", m = "  ", y = "  ";

        if (value) {
            const str = String(value).trim();
            if (str.includes("/")) {
                const parts = str.split("/");
                d = (parts[0] || "").padStart(2, "0").slice(-2);
                m = (parts[1] || "").padStart(2, "0").slice(-2);
                y = (parts[2] || "").slice(-2).padStart(2, "0");
            } else {
                const digits = str.replace(/\D/g, "");
                if (digits.length >= 6) {
                    d = digits.slice(0, 2);
                    m = digits.slice(2, 4);
                    y = digits.slice(-2);
                }
            }
        }

        return (
            <div style={{ display: "flex", width: "100%", height: "100%", alignItems: "flex-end" }}>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictCombReadOnly length={2} value={d} />
                </div>
                <div style={{
                    width: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: color,
                    paddingBottom: "2px"
                }}>/</div>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictCombReadOnly length={2} value={m} />
                </div>
                <div style={{
                    width: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: color,
                    paddingBottom: "2px"
                }}>/</div>
                <div style={{ flex: 2, display: "flex", height: "100%" }}>
                    <StrictCombReadOnly length={2} value={y} />
                </div>
            </div>
        );
    };

    return (
        <div className="w-100 d-flex flex-column" style={{ minHeight: "100%" }}>
            <style>{`
                :root {
                    --f5-color: #009EE2;
                    --f5-box: #FFC857;
                    --f5-box-column: #FFD275;
                }
                .f5-document {
                    font-family: Arial, Helvetica, sans-serif;
                    color: var(--f5-color);
                    border: 2px solid var(--f5-color);
                    box-sizing: border-box;
                    background-color: #fff;
                    width: 1060px;
                    margin: 0 auto;
                    position: relative;
                }
                .f5-document * {
                    box-sizing: border-box;
                }
                .f5-code-box {
                    width: 40px;
                    min-width: 40px;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 0.8rem;
                    color: var(--f5-color);
                    background-color: #fff;
                    border-right: 1px solid var(--f5-color);
                    flex-shrink: 0;
                }
                .f5-code-box.yellow {
                    background-color: var(--f5-box);
                }
                .f5-cell-text {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    font-size: 0.8rem;
                    font-weight: bold;
                    color: #000;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }
                .f5-row {
                    display: flex;
                    width: 100%;
                    border-bottom: 1px solid var(--f5-color);
                }
                .f5-custom-checkbox {
                    -webkit-appearance: none;
                    appearance: none;
                    margin: 0;
                    width: 16px;
                    height: 16px;
                    border: 1px solid var(--f5-color);
                    background-color: #fff;
                    display: inline-grid;
                    place-content: center;
                    outline: none;
                }
                .f5-custom-checkbox:checked::before {
                    content: "";
                    width: 5px;
                    height: 10px;
                    border: solid #000;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                    margin-bottom: 2px;
                }
                .f5-dotted-line {
                    border-bottom: 1.5px dotted var(--f5-color);
                    height: 16px;
                    margin: 0 4px;
                    flex-grow: 1;
                }
                .vertical-text {
                    writing-mode: vertical-rl;
                    transform: rotate(180deg);
                    text-align: center;
                    padding: 4px;
                    margin: 0 auto;
                }
            `}</style>

            <div
                className="w-100 py-3"
                style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "auto"
                }}
                onMouseDown={(e) => {
                    if (zoomLevel <= 1) return;
                    setIsDragging(true);
                    setDragStartX(e.clientX);
                    setDragScrollLeft(e.currentTarget.scrollLeft);
                }}
                onMouseLeave={() => setIsDragging(false)}
                onMouseUp={() => setIsDragging(false)}
                onMouseMove={(e) => {
                    if (!isDragging) return;
                    e.preventDefault();
                    const walk = (e.clientX - dragStartX) * 1.5;
                    e.currentTarget.scrollLeft = dragScrollLeft - walk;
                }}
            >
                <div style={{ width: "1060px", margin: "0 auto", userSelect: isDragging ? "none" : "auto" }}>
                    <div className="f5-document shadow-sm" style={{ zoom: zoomLevel }}>
                        <div className="position-relative w-100" style={{ height: "185px", borderBottom: "2px solid var(--f5-color)" }}>
                            <div style={{ position: "absolute", top: 0, left: 0, width: "145px", height: "145px", zIndex: 1, pointerEvents: "none" }}>
                                <svg viewBox="0 0 145 145" width="100%" height="100%">
                                    <defs>
                                        <clipPath id="f5-view-triangle-clip">
                                            <polygon points="0,0 145,0 0,145" />
                                        </clipPath>
                                    </defs>

                                    <g clipPath="url(#f5-view-triangle-clip)">
                                        <polygon points="0,0 145,0 0,145" fill="var(--f5-color)" />

                                        <text x="12" y="43" fill="#fff" fontSize="42" fontFamily="'Times New Roman', Times, serif" fontWeight="normal">
                                            Φ
                                        </text>
                                        <text x="40" y="46" fill="#fff" fontSize="27" fontFamily="'Times New Roman', Times, serif" fontWeight="bold">
                                            5
                                        </text>

                                        <g transform="translate(64, 72) rotate(-45)">
                                            <line x1="-85" y1="-12" x2="85" y2="-12" stroke="#fff" strokeWidth="1.3" opacity="0.95" />
                                            <text
                                                x="0"
                                                y="4.5"
                                                textAnchor="middle"
                                                fill="#fff"
                                                fontSize="16"
                                                fontWeight="900"
                                                fontFamily="Arial, sans-serif"
                                                letterSpacing="9"
                                            >
                                                TAXIS
                                            </text>
                                            <line x1="-85" y1="12" x2="85" y2="12" stroke="#fff" strokeWidth="1.3" opacity="0.95" />
                                        </g>
                                    </g>
                                </svg>
                            </div>

                            <div style={{ position: "absolute", left: "205px", top: "15px", width: "470px" }}>
                                <div style={{ fontSize: "1.05rem", fontWeight: "bold", color: "var(--f5-color)", letterSpacing: "0.5px" }}>
                                    ΑΝΑΚΕΦΑΛΑΙΩΤΙΚΟΣ ΠΙΝΑΚΑΣ
                                </div>
                                <div style={{ fontSize: "0.8rem", fontWeight: "bold", color: "var(--f5-color)", marginTop: "4px", lineHeight: "1.3" }}>
                                    ΕΝΔΟΚΟΙΝΟΤΙΚΩΝ ΑΠΟΚΤΗΣΕΩΝ ΑΓΑΘΩΝ ΚΑΙ ΛΗΨΕΩΝ ΥΠΗΡΕΣΙΩΝ
                                </div>
                                <div style={{ fontSize: "0.7rem", color: "var(--f5-color)", marginTop: "8px", textAlign: "left" }}>
                                    Υποβάλλεται σε δύο (2) αντίτυπα (έντυπη υποβολή)
                                </div>
                            </div>

                            <div style={{ position: "absolute", right: "12px", top: "15px", width: "320px" }}>
                                <div style={{ display: "flex", alignItems: "flex-end", marginBottom: "8px" }}>
                                    <div style={{
                                        fontSize: "0.55rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "right",
                                        width: "100px",
                                        paddingRight: "8px",
                                        lineHeight: "1.1"
                                    }}>
                                        ΑΡΙΘΜΟΣ<br />ΔΗΛΩΣΗΣ
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        border: "1px solid var(--f5-color)",
                                        height: "32px",
                                        flexGrow: 1,
                                        position: "relative"
                                    }}>
                                        <div className="f5-code-box yellow" style={{ width: "34px", minWidth: "34px" }}>
                                            003
                                        </div>
                                        <div style={{ flex: 2.2, borderRight: "1px solid var(--f5-color)" }}>
                                            <StrictCombReadOnly
                                                length={6}
                                                value={decl.protocolNumber || decl.declarationNumber || fields.protocolNumber || fields["003"] || ""}
                                                label="ΑΡΙΘΜΟΣ"
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <StrictCombReadOnly length={2} value={submissionYearShort} label="ΕΤΟΣ" />
                                        </div>
                                        <span style={{
                                            position: "absolute",
                                            right: "-8px",
                                            top: "-4px",
                                            color: "var(--f5-color)",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold"
                                        }}>*</span>
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "flex-end" }}>
                                    <div style={{
                                        fontSize: "0.55rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "right",
                                        width: "100px",
                                        paddingRight: "8px",
                                        lineHeight: "1.1"
                                    }}>
                                        ΗΜΕΡΟΜΗΝΙΑ<br />ΥΠΟΒΟΛΗΣ ΔΗΛΩΣΗΣ
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        border: "1px solid var(--f5-color)",
                                        height: "24px",
                                        flexGrow: 1,
                                        position: "relative"
                                    }}>
                                        <div className="f5-code-box" style={{ width: "34px", minWidth: "34px" }}>004</div>
                                        <DateCombSlash value={submissionDateSlash} color="var(--f5-color)" />
                                        <span style={{
                                            position: "absolute",
                                            right: "-8px",
                                            top: "-4px",
                                            color: "var(--f5-color)",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold"
                                        }}>*</span>
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                position: "absolute",
                                left: "140px",
                                top: "115px",
                                display: "flex",
                                alignItems: "flex-end",
                                width: "460px"
                            }}>
                                <div style={{
                                    fontSize: "0.8rem",
                                    fontWeight: "bold",
                                    color: "var(--f5-color)",
                                    paddingBottom: "2px"
                                }}>Δ.Ο.Υ.</div>
                                <div className="f5-dotted-line d-flex align-items-end">
                                    <span style={{
                                        fontSize: "0.75rem",
                                        fontWeight: "bold",
                                        color: "#000",
                                        paddingLeft: "8px"
                                    }}>{doyLabel}</span>
                                </div>
                                <div style={{
                                    display: "flex",
                                    border: "1px solid var(--f5-color)",
                                    height: "24px",
                                    width: "135px",
                                    position: "relative",
                                    marginLeft: "6px"
                                }}>
                                    <div className="f5-code-box" style={{ width: "38px", minWidth: "38px" }}>001</div>
                                    <StrictCombReadOnly length={4} value={doyCode} />
                                    <span style={{
                                        position: "absolute",
                                        right: "-8px",
                                        top: "-4px",
                                        color: "var(--f5-color)",
                                        fontSize: "0.75rem",
                                        fontWeight: "bold"
                                    }}>*</span>
                                </div>
                            </div>

                            <div style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                width: "100%",
                                display: "flex",
                                alignItems: "flex-end"
                            }}>
                                <div style={{ width: "420px" }}>
                                    <div style={{
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "center",
                                        marginBottom: "2px"
                                    }}>
                                        Ημερολογιακή περίοδος
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        borderTop: "1px solid var(--f5-color)",
                                        borderRight: "1px solid var(--f5-color)",
                                        height: "28px"
                                    }}>
                                        <div style={{
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            color: "var(--f5-color)"
                                        }}>από</div>
                                        <div className="f5-code-box yellow" style={{
                                            width: "36px",
                                            minWidth: "36px",
                                            borderLeft: "1px solid var(--f5-color)"
                                        }}>006</div>
                                        <div style={{ flex: 1 }}>
                                            <DateCombSlash value={periodFromSlash} color="var(--f5-color)" />
                                        </div>
                                        <div style={{
                                            padding: "0 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            color: "var(--f5-color)",
                                            borderLeft: "1px solid var(--f5-color)",
                                            borderRight: "1px solid var(--f5-color)"
                                        }}>έως</div>
                                        <div style={{ flex: 1 }}>
                                            <DateCombSlash value={periodToSlash} color="var(--f5-color)" />
                                        </div>
                                    </div>
                                </div>

                                <div style={{
                                    display: "flex",
                                    borderTop: "1px solid var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)",
                                    height: "42px",
                                    marginLeft: "15px"
                                }}>
                                    <div className="f5-code-box yellow" style={{ borderLeft: "1px solid var(--f5-color)", width: "38px", minWidth: "38px" }}>007</div>
                                    <div style={{ width: "48px", display: "flex", flexDirection: "column" }}>
                                        <div style={{
                                            fontSize: "0.45rem",
                                            fontWeight: "bold",
                                            color: "var(--f5-color)",
                                            textAlign: "center",
                                            borderBottom: "1px solid var(--f5-color)",
                                            lineHeight: "1.1",
                                            padding: "2px 0"
                                        }}>
                                            ΔΙΟΡΘΩ-<br />ΤΙΚΟΣ
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            flexGrow: 1,
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            <input
                                                type="checkbox"
                                                className="f5-custom-checkbox"
                                                checked={isAmending}
                                                disabled={true}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ width: "210px", marginLeft: "15px" }}>
                                    <div style={{
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "center",
                                        marginBottom: "2px"
                                    }}>
                                        Μήνας
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        borderTop: "1px solid var(--f5-color)",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderLeft: "1px solid var(--f5-color)",
                                        height: "28px"
                                    }}>
                                        <div className="f5-code-box yellow" style={{ width: "38px", minWidth: "38px" }}>
                                            009
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexGrow: 1,
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                            color: "#000"
                                        }}>
                                            {selectedMonthGreek}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ flexGrow: 1, marginLeft: "15px" }}>
                                    <div style={{
                                        fontSize: "0.7rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "center",
                                        marginBottom: "2px"
                                    }}>
                                        Τρίμηνο / έτος
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        borderTop: "1px solid var(--f5-color)",
                                        borderLeft: "1px solid var(--f5-color)",
                                        height: "28px"
                                    }}>
                                        <div className="f5-code-box yellow" style={{ width: "38px", minWidth: "38px" }}>
                                            008
                                        </div>
                                        <div style={{ width: "35px" }}>
                                            <StrictCombReadOnly length={1} value={String(selectedQuarter)} />
                                        </div>
                                        <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "24px",
                                            fontSize: "1.2rem",
                                            color: "var(--f5-color)",
                                            borderLeft: "1px solid var(--f5-color)",
                                            borderRight: "1px solid var(--f5-color)"
                                        }}>
                                            /
                                        </div>
                                        <div style={{ flexGrow: 1 }}>
                                            <StrictCombReadOnly length={4} value={String(selectedYear)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-100" style={{ borderBottom: "2px solid var(--f5-color)" }}>
                            <div style={{
                                padding: "4px 8px",
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                color: "var(--f5-color)",
                                borderBottom: "1px solid var(--f5-color)"
                            }}>
                                Α. ΠΙΝΑΚΑΣ ΜΕ ΤΑ ΣΤΟΙΧΕΙΑ ΤΟΥ ΔΗΛΟΥΝΤΟΣ
                            </div>
                            <div className="f5-row" style={{ height: "30px" }}>
                                <div className="f5-code-box">011</div>
                                <div style={{
                                    width: "160px",
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    ΕΠΩΝΥΜΟ 'Η ΕΠΩΝΥΜΙΑ
                                </div>
                                <div style={{
                                    flexGrow: 1,
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.75rem",
                                    color: "#000"
                                }}>
                                    {decl.companyName || companyDetails?.name || company?.name || ""}
                                </div>
                            </div>
                            <div className="f5-row" style={{ height: "32px" }}>
                                <div className="f5-code-box">012</div>
                                <div style={{
                                    width: "70px",
                                    padding: "0 6px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    ΟΝΟΜΑ
                                </div>
                                <div style={{ width: "280px", borderRight: "1px solid var(--f5-color)", padding: "0 8px" }}>
                                    <div className="f5-cell-text">{fields["012"] || decl.firstName || ""}</div>
                                </div>

                                <div className="f5-code-box">013</div>
                                <div style={{
                                    width: "100px",
                                    padding: "0 6px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)",
                                    lineHeight: "1.1"
                                }}>
                                    ΟΝΟΜΑ ΠΑΤΕΡΑ
                                </div>
                                <div style={{ width: "230px", borderRight: "1px solid var(--f5-color)", padding: "0 8px" }}>
                                    <div className="f5-cell-text">{fields["013"] || decl.fatherName || ""}</div>
                                </div>

                                <div className="f5-code-box">014</div>
                                <div style={{
                                    width: "55px",
                                    padding: "0 6px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    ΤΙΤΛΟΣ
                                </div>
                                <div style={{
                                    flexGrow: 1,
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.7rem",
                                    color: "#000",
                                    overflow: "hidden"
                                }}>
                                    {fields["014"] || decl.title || companyDetails?.title || company?.name || ""}
                                </div>
                            </div>
                            <div className="f5-row" style={{ height: "32px" }}>
                                <div className="f5-code-box">015</div>
                                <div style={{
                                    width: "175px",
                                    padding: "0 6px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.6rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)",
                                    lineHeight: "1.1"
                                }}>
                                    Δ/ΝΣΗ: ΟΔΟΣ ΑΡΙΘΜΟΣ 'Η ΤΟΠΟΘΕΣΙΑ - ΧΩΡΙΟ
                                </div>
                                <div style={{
                                    width: "260px",
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.75rem",
                                    color: "#000",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    {fields["015"] || decl.address || companyDetails?.address || ""}
                                </div>

                                <div className="f5-code-box">016</div>
                                <div style={{
                                    width: "140px",
                                    padding: "0 6px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    color: "var(--f5-color)",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    ΔΗΜΟΣ 'Η ΚΟΙΝΟΤΗΤΑ
                                </div>
                                <div style={{
                                    width: "185px",
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontWeight: "bold",
                                    fontSize: "0.75rem",
                                    color: "#000",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    {fields["016"] || decl.city || companyDetails?.city || ""}
                                </div>

                                <div className="f5-code-box">017</div>
                                <div style={{
                                    flexGrow: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}>
                                    <StrictCombReadOnly
                                        length={5}
                                        value={fields["017"] || decl.postalCode || companyDetails?.postalCode || ""}
                                        label="ΤΑΧ. ΚΩΔ"
                                    />
                                </div>
                            </div>
                            <div className="f5-row" style={{ height: "36px" }}>
                                <div style={{
                                    width: "216px",
                                    padding: "0 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    borderRight: "1px solid var(--f5-color)"
                                }}>
                                    <span style={{ fontWeight: "bold", fontSize: "0.65rem", color: "var(--f5-color)" }}>ΑΡΙΘΜΟΙ ΤΗΛΕΦΩΝΟΥ - FAX</span>
                                    <div style={{
                                        width: 0,
                                        height: 0,
                                        borderTop: "4px solid transparent",
                                        borderBottom: "4px solid transparent",
                                        borderLeft: "7px solid var(--f5-color)"
                                    }}></div>
                                </div>

                                <div className="f5-code-box">018</div>
                                <div style={{ width: "95px", borderRight: "1px solid var(--f5-color)" }}>
                                    <StrictCombReadOnly
                                        length={4}
                                        value={fields["018_code"] || decl.phoneCode || ""}
                                        label="ΚΩΔΙΚΟΣ"
                                    />
                                </div>
                                <div style={{ width: "235px", borderRight: "1px solid var(--f5-color)" }}>
                                    <StrictCombReadOnly
                                        length={10}
                                        value={fields["018"] || decl.phone || companyDetails?.phone || ""}
                                        label="ΑΡΙΘΜΟΣ ΤΗΛΕΦΩΝΟΥ"
                                    />
                                </div>

                                <div className="f5-code-box">019</div>
                                <div style={{ width: "95px", borderRight: "1px solid var(--f5-color)" }}>
                                    <StrictCombReadOnly
                                        length={4}
                                        value={fields["019_code"] || decl.faxCode || ""}
                                        label="ΚΩΔΙΚΟΣ"
                                    />
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <StrictCombReadOnly
                                        length={10}
                                        value={fields["019"] || decl.fax || ""}
                                        label="ΑΡΙΘΜΟΣ FAX"
                                    />
                                </div>
                            </div>
                            <div className="f5-row" style={{ height: "55px", borderBottom: "none" }}>
                                <div className="f5-code-box yellow" style={{ fontSize: "0.85rem" }}>020</div>
                                <div style={{
                                    width: "320px",
                                    borderRight: "1px solid var(--f5-color)",
                                    display: "flex",
                                    flexDirection: "column"
                                }}>
                                    <div style={{
                                        fontSize: "0.6rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "center",
                                        paddingTop: "2px"
                                    }}>
                                        Α.Φ.Μ.
                                    </div>
                                    <div style={{ display: "flex", flexGrow: 1 }}>
                                        <div style={{
                                            padding: "0 10px",
                                            fontSize: "0.8rem",
                                            fontWeight: "bold",
                                            color: "var(--f5-color)",
                                            borderRight: "1px solid var(--f5-color)",
                                            display: "flex",
                                            alignItems: "flex-end",
                                            paddingBottom: "2px"
                                        }}>
                                            E , L
                                        </div>
                                        <div style={{ flexGrow: 1 }}>
                                            <StrictCombReadOnly
                                                length={9}
                                                value={decl.companyVatNumber || decl.companyAfm || company?.vatNumber || ""}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="f5-code-box">021</div>
                                <div style={{
                                    width: "380px",
                                    borderRight: "1px solid var(--f5-color)",
                                    display: "flex",
                                    flexDirection: "column"
                                }}>
                                    <div style={{
                                        fontSize: "0.6rem",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        textAlign: "center",
                                        paddingTop: "2px"
                                    }}>
                                        ΑΡ. ΦΑΚΕΛΟΥ
                                    </div>
                                    <div style={{ display: "flex", flexGrow: 1 }}>
                                        <StrictCombReadOnly
                                            length={10}
                                            value={fields["021"] || decl.fileNumber || ""}
                                        />
                                    </div>
                                </div>
                                <div style={{ flexGrow: 1, display: "flex" }}>
                                    <div style={{
                                        flex: 1,
                                        borderRight: "1px solid var(--f5-color)",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        padding: "4px 0",
                                        textAlign: "center"
                                    }}>
                                        <div style={{
                                            fontSize: "0.55rem",
                                            fontWeight: "bold",
                                            color: "var(--f5-color)",
                                            lineHeight: "1.1"
                                        }}>Αριθμός<br />σελίδας</div>
                                        <div style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#000" }}>
                                            {decl.pageNumber || 1}
                                        </div>
                                    </div>

                                    <div className="f5-code-box yellow" style={{
                                        width: "36px",
                                        minWidth: "36px",
                                        borderRight: "1px solid var(--f5-color)"
                                    }}>023</div>

                                    <div style={{ flex: 2, display: "flex", position: "relative" }}>
                                        <div style={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "4px 0"
                                        }}>
                                            <span style={{
                                                fontSize: "0.6rem",
                                                fontWeight: "bold",
                                                color: "var(--f5-color)"
                                            }}>α/α</span>
                                            <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#000" }}>
                                                {decl.pageIndex || 1}
                                            </span>
                                        </div>
                                        <svg style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%"
                                        }} preserveAspectRatio="none">
                                            <line x1="30%" y1="100%" x2="70%" y2="0" stroke="var(--f5-color)" strokeWidth="1.2" />
                                        </svg>
                                        <div style={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            padding: "4px 0"
                                        }}>
                                            <span style={{
                                                fontSize: "0.55rem",
                                                fontWeight: "bold",
                                                paddingLeft: "10px",
                                                color: "var(--f5-color)",
                                                textAlign: "center",
                                                lineHeight: "1.1"
                                            }}>Σύνολο<br />σελίδων</span>
                                            <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#000" }}>
                                                {decl.totalPages || 1}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-100" style={{ borderBottom: "2px solid var(--f5-color)" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center" }}>
                                <thead>
                                <tr>
                                    <th colSpan={7} style={{
                                        padding: "6px 8px",
                                        fontWeight: "bold",
                                        fontSize: "0.85rem",
                                        color: "var(--f5-color)",
                                        textAlign: "left",
                                        borderBottom: "1px solid var(--f5-color)"
                                    }}>
                                        Β. ΠΙΝΑΚΑΣ ΕΝΔΟΚΟΙΝΟΤΙΚΩΝ ΑΠΟΚΤΗΣΕΩΝ ΑΓΑΘΩΝ ΚΑΙ ΛΗΨΕΩΝ ΥΠΗΡΕΣΙΩΝ
                                    </th>
                                </tr>

                                <tr>
                                    <th rowSpan={2} style={{
                                        width: "4%",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.65rem",
                                        fontWeight: "bold"
                                    }}>
                                        α/α<br /><br />(1)
                                    </th>
                                    <th colSpan={3} style={{
                                        width: "46%",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.7rem",
                                        fontWeight: "bold"
                                    }}>
                                        Στοιχεία προμηθευτή / παρέχοντος υπηρεσίες
                                    </th>
                                    <th colSpan={3} style={{
                                        width: "50%",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.7rem",
                                        fontWeight: "bold"
                                    }}>
                                        Φορολογητέα Αξία**
                                    </th>
                                </tr>

                                <tr>
                                    <th style={{
                                        width: "16%",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.65rem",
                                        fontWeight: "bold",
                                        padding: "4px"
                                    }}>
                                        Κ-Μ προμηθευτή /<br />παρέχοντος υπηρεσίες<br /><br />(2)
                                    </th>
                                    <th style={{
                                        width: "6%",
                                        backgroundColor: "var(--f5-box)",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.6rem",
                                        fontWeight: "bold",
                                        padding: "4px"
                                    }}>
                                        Πρόθεμα<br />Κ-Μ<br /><br />(3)
                                    </th>
                                    <th style={{
                                        width: "24%",
                                        backgroundColor: "var(--f5-box)",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.65rem",
                                        fontWeight: "bold",
                                        padding: "4px"
                                    }}>
                                        Αριθμός μητρώου ΦΠΑ<br /><br />(4)
                                    </th>
                                    <th style={{
                                        width: "16%",
                                        backgroundColor: "var(--f5-box)",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.6rem",
                                        fontWeight: "bold",
                                        padding: "4px"
                                    }}>
                                        Ενδοκοινοτικών<br />αποκτήσεων αγαθών<br /><br />(5)
                                    </th>
                                    <th style={{
                                        width: "17%",
                                        backgroundColor: "var(--f5-box-column)",
                                        borderRight: "1px solid var(--f5-color)",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.55rem",
                                        fontWeight: "bold",
                                        padding: "3px 4px",
                                        lineHeight: "1.1"
                                    }}>
                                        Αποκτήσεων από άλλα κράτη-μέλη που προορίζονται για παράδοση σε άλλο κράτος-μέλος (τριγωνικές συναλλαγές)<br />(6)
                                    </th>
                                    <th style={{
                                        width: "17%",
                                        borderBottom: "1px solid var(--f5-color)",
                                        color: "var(--f5-color)",
                                        fontSize: "0.6rem",
                                        fontWeight: "bold",
                                        padding: "4px"
                                    }}>
                                        Ενδοκοινοτικών<br />λήψεων υπηρεσιών (B2B)<br /><br />(7)
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {filledTableB.map((row, idx) => {
                                    const rawCode = String(row.countryCode || "").trim().toUpperCase();
                                    const rawDesc = String(row.ctryDesc || "").trim().toUpperCase();
                                    const selectedCountryObj = EU_COUNTRIES.find(c =>
                                        c.code === rawCode || c.label === rawDesc || c.label === rawCode
                                    );
                                    const countryLabel = selectedCountryObj ? selectedCountryObj.label : (row.ctryDesc || "");
                                    const countryCode = selectedCountryObj ? selectedCountryObj.code : (rawCode || "");

                                    const valBc5 = parseFloat(String(row.bc5 || "").replace(",", "."));
                                    const valBc6 = parseFloat(String(row.bc6 || "").replace(",", "."));
                                    const valBc7 = parseFloat(String(row.bc7 || "").replace(",", "."));

                                    return (
                                        <tr key={idx} style={{ height: "26px", borderBottom: "1px solid var(--f5-color)" }}>
                                            <td style={{
                                                borderRight: "1px solid var(--f5-color)",
                                                fontWeight: "bold",
                                                color: "var(--f5-color)",
                                                fontSize: "0.75rem"
                                            }}>
                                                {row.lineNo || (idx + 1)}
                                            </td>
                                            <td style={{ borderRight: "1px solid var(--f5-color)", padding: "0 6px" }}>
                                                <div className="f5-cell-text justify-content-center" style={{ fontSize: "0.8rem" }}>
                                                    {countryLabel}
                                                </div>
                                            </td>
                                            <td style={{ borderRight: "1px solid var(--f5-color)", padding: 0 }}>
                                                <StrictCombReadOnly length={2} value={countryCode} />
                                            </td>
                                            <td style={{ borderRight: "1px solid var(--f5-color)", padding: 0 }}>
                                                <StrictCombReadOnly
                                                    length={12}
                                                    value={row.bc4 || ""}
                                                />
                                            </td>
                                            <td style={{ borderRight: "1px solid var(--f5-color)", padding: "0 6px", textAlign: "right" }}>
                                                <span className="font-weight-bold text-dark" style={{ fontSize: "0.75rem" }}>
                                                    {valBc5 ? getGreekCurrencyFormat(valBc5) : ""}
                                                </span>
                                            </td>
                                            <td style={{
                                                borderRight: "1px solid var(--f5-color)",
                                                backgroundColor: "var(--f5-box-column)",
                                                padding: "0 6px",
                                                textAlign: "right"
                                            }}>
                                                <span className="font-weight-bold text-dark" style={{ fontSize: "0.75rem" }}>
                                                    {valBc6 ? getGreekCurrencyFormat(valBc6) : ""}
                                                </span>
                                            </td>
                                            <td style={{ padding: "0 6px", textAlign: "right" }}>
                                                <span className="font-weight-bold text-dark" style={{ fontSize: "0.75rem" }}>
                                                    {valBc7 ? getGreekCurrencyFormat(valBc7) : ""}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                <tr style={{ height: "30px" }}>
                                    <td colSpan={4} style={{
                                        borderRight: "1px solid var(--f5-color)",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        color: "var(--f5-color)",
                                        fontSize: "0.8rem",
                                        letterSpacing: "1px"
                                    }}>
                                        ΣΥΝΟΛΑ ΣΕΛΙΔΑΣ
                                    </td>
                                    <td style={{
                                        borderRight: "1px solid var(--f5-color)",
                                        textAlign: "right",
                                        paddingRight: "6px",
                                        fontWeight: "bold",
                                        fontSize: "0.75rem",
                                        color: "#000"
                                    }}>
                                        {getGreekCurrencyFormat(sumBc5)}
                                    </td>
                                    <td style={{
                                        borderRight: "1px solid var(--f5-color)",
                                        backgroundColor: "var(--f5-box-column)",
                                        textAlign: "right",
                                        paddingRight: "6px",
                                        fontWeight: "bold",
                                        fontSize: "0.75rem",
                                        color: "#000"
                                    }}>
                                        {getGreekCurrencyFormat(sumBc6)}
                                    </td>
                                    <td style={{
                                        textAlign: "right",
                                        paddingRight: "6px",
                                        fontWeight: "bold",
                                        fontSize: "0.75rem",
                                        color: "#000"
                                    }}>
                                        {getGreekCurrencyFormat(sumBc7)}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center px-1 mt-1 mb-1" style={{ fontSize: "0.5rem" }}>
                            <div className="d-flex align-items-center">
                                <div className="vertical-text mr-2 font-weight-bold" style={{ height: "40px", fontSize: "0.45rem", color: "var(--f5-color)" }}>
                                    Γ.Γ.Π.Σ. - ΔΛΟΣΕΛΥ
                                </div>
                                <div className="d-flex flex-column" style={{ lineHeight: 1.2 }}>
                                    <span style={{ color: "var(--f5-color)" }}>Συμπληρώνεται από τη ΔΟΥ</span>
                                    <span style={{ color: "var(--f5-color)" }}>** Τα ποσά αναγράφονται ΥΠΟΧΡΕΩΤΙΚΑ ΣΕ ΕΥΡΩ με τη χρήση δύο (2) δεκαδικών ψηφίων μετά την υποδιαστολή, π.χ. 45,00 ή 45,65.</span>
                                </div>
                            </div>
                            <span className="font-weight-bold" style={{ fontSize: "0.6rem", color: "var(--f5-color)" }}>ΕΚΔΟΣΗ 2020</span>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className="w-100 position-sticky bg-white border-top py-1 px-3 d-flex justify-content-start align-items-center mt-auto print-hide"
                style={{
                    bottom: 0,
                    zIndex: 3,
                    boxShadow: "0 -4px 10px -2px rgba(0,0,0,0.1)"
                }}
            >
                <span className="mr-2 text-dark font-weight-bold" style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                    Zoom:
                </span>
                <Button
                    variant="light"
                    size="sm"
                    className="py-1 px-2 border font-weight-bold"
                    onClick={() => setZoomLevel(prev => Math.max(Number((prev - 0.1).toFixed(1)), 0.4))}
                >
                    -
                </Button>
                <span className="mx-2 font-weight-bold text-primary" style={{ minWidth: "35px", textAlign: "center", fontSize: "0.75rem" }}>
                    {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                    variant="light"
                    size="sm"
                    className="py-1 px-2 border font-weight-bold"
                    onClick={() => setZoomLevel(prev => Math.min(Number((prev + 0.1).toFixed(1)), 2.0))}
                >
                    +
                </Button>
                {zoomLevel !== 1 && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-1 px-1 ml-2 border"
                        onClick={() => setZoomLevel(1)}
                        title="Επαναφορά στο 100%"
                    >
                        <i className="fa-solid fa-arrow-rotate-left"></i>
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AccountingF5VatDeclarationView;